import { redirect } from 'react-router';
import { ENGLISH_LOCALE } from '@link/share/constants';
import { SHUTTO_LOCALE_VALUES } from '@link/skytree/i18n';

const storage = sessionStorage;
export const STT_LANG_PARAM = 'sttLang';

const isValidSttLang = (value: string) =>
  (SHUTTO_LOCALE_VALUES as readonly string[]).includes(value);

/** Replace the first path segment (locale) with English if it differs. */
function normalizeToEnglishPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] && segments[0] !== ENGLISH_LOCALE) {
    segments[0] = ENGLISH_LOCALE;
    return '/' + segments.join('/');
  }
  return pathname;
}

function buildUrl(
  pathname: string,
  searchParams: URLSearchParams,
  hash: string
): string {
  const queryString = searchParams.toString();
  return `${pathname}${queryString ? `?${queryString}` : ''}${hash}`;
}

export function setSttLang(value: string) {
  storage.setItem(STT_LANG_PARAM, value);
}

export function getSttLang() {
  return storage.getItem(STT_LANG_PARAM);
}

export function removeSttLang() {
  storage.removeItem(STT_LANG_PARAM);
}

/**
 * The active Shutto language, resolved from the URL param when present and
 * from session storage otherwise. The param is no longer written back into
 * every URL, so session storage is the source of truth. Callers pass the
 * param value they observe so the result stays reactive to navigation.
 */
export function resolveSttLang(paramValue: string | null): string | null {
  if (paramValue) {
    return isValidSttLang(paramValue) ? paramValue : null;
  }
  const storedSttLang = getSttLang();
  return storedSttLang && isValidSttLang(storedSttLang) ? storedSttLang : null;
}

/**
 * Captures the Shutto language from the entry URL and keeps the locale segment
 * on English, which is the language Shutto translates from.
 *
 * It redirects only to change the path, never to change the query: react-router
 * builds a redirect location with `{ _isRedirect: true }` as its state,
 * replacing the state of the navigation it interrupts, which drops things like
 * the `isNewUser` flag carried into the email-verification page. So the stored
 * language is never written back into the URL and an invalid one is never
 * stripped from it - `resolveSttLang` reads session storage and ignores an
 * invalid param, so neither needs the URL to be tidy. A path change is the one
 * case a navigation is unavoidable.
 *
 * No in-app navigation reaches that remaining path redirect, and the redirect
 * itself is what makes that true: a valid stored language implies an English
 * locale segment, because every arrival at a non-English path with one stored
 * is pulled to /en here, and `changeLocale` only leaves an English path after
 * clearing the stored language. The Base layout keeps `i18n.language` in step
 * with the locale segment, so while Shutto is active `buildLocalizedPath` and
 * relative targets resolve to /en paths as well.
 *
 * It is reached by entry URLs, which carry no state, and by back/forward to a
 * non-English history entry recorded before Shutto was switched on. That entry
 * can carry state (e.g. email verification's `isNewUser`), and the redirect
 * drops it. A loader cannot pass state through a redirect, so that loss is
 * accepted: it takes a Shutto switch followed by going back past it.
 *
 * So do not drop it to "avoid redirecting during a navigation": without it the
 * app can sit on /ja with Shutto active, which makes Shutto translate from
 * Japanese rather than English and breaks the invariant the stateful
 * navigations rely on.
 */
export function sttLangLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const sttLang = searchParams.get(STT_LANG_PARAM);

  if (sttLang) {
    if (isValidSttLang(sttLang)) {
      setSttLang(sttLang);
    } else {
      removeSttLang();
    }
  }

  const activeSttLang = resolveSttLang(sttLang);
  if (activeSttLang) {
    const enPath = normalizeToEnglishPath(url.pathname);
    if (enPath !== url.pathname) {
      throw redirect(buildUrl(enPath, searchParams, url.hash));
    }
  }

  return null;
}
